import i18next from 'i18next';
import _ from 'lodash';
import tinycolor from 'tinycolor2';
import {
  sqDurationStore,
  sqScorecardStore,
  sqTableBuilderStore,
  sqTreemapStore,
  sqTrendStore,
  sqWorkbenchStore,
  sqWorksheetStore,
} from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { getCapsuleFormula, nanosToMillis } from '@/datetime/dateTime.utilities';
import { MAX_TABLE_ROWS } from '@/main/app.constants';
import { cancelGroup } from '@/requests/pendingRequests.utilities';
import { swapAsset } from '@/search/searchResult.utilities.service';
import { SAMPLE_FROM_SCALARS } from '@/services/calculationRunner.constants';
import { priorityColors, workbenchTextContextEnrichment } from '@/services/systemConfiguration.utilities';
import {
  buildConditionTableColumnRequestFields,
  normalizeConditionTableResponse,
} from '@/tableBuilder/conditionTableData.utilities';
import {
  ASSET_PATH_COLUMN_PREFIX,
  ASSET_PATH_ID_COLUMN_PREFIX,
  MAX_CONDITION_TABLE_CAPSULES,
  TableBuilderColumnType,
  TableBuilderHeaderType,
  TableBuilderMode,
} from '@/tableBuilder/tableBuilder.constants';
import { createAssetPathConfigs, isScaleLockedByTreemap, tableBuilder } from '@/tableBuilder/tableBuilder.utilities';
import { resolveScalingColumnNamesForItems } from '@/tableDefinitionEditor/materializedTable.utilities';
import { METRIC_COLORS } from '@/toolSelection/investigate.constants';
import { getAllItems, isScalingTableItem } from '@/trend/trendDataHelper.utilities';
import { getDurationDisplayRange } from '@/trendData/duration.utilities';
import {
  addTrendItem,
  catchItemDataFailure,
  setItemSelected,
  setItemStatusNotRequired,
  setTrendSelectedRegion,
  toggleHideUnselectedItems,
} from '@/trendData/trend.actions';
import { COLUMNS_AND_STATS, ITEM_TYPES } from '@/trendData/trendData.constants';
import {
  setDataStatusLoadingInTrendStores,
  setDataStatusPresentInTrendStores,
} from '@/trendData/trendItemStores.actions';
import { alignMeasuredItemWithMetric } from '@/trendData/trendMetric.actions';
import { getStatisticFragment } from '@/utilities/calculationRunner.utilities';
import { DUMMY_ITEM_PREFIX } from '@/utilities/formula.constants';
import {
  canFetchData,
  computeCapsuleTable,
  computeScalar,
  computeTable,
  getDefaultName,
  getDependencies,
} from '@/utilities/formula.utilities';
import { extractGuids, validateGuid } from '@/utilities/guid.utilities';
import { isCanceled } from '@/utilities/http.utilities';
import { formatNumber } from '@/utilities/numberHelper.utilities';
import { isItemRedacted } from '@/utilities/redaction.utilities';
import { AUTO_CLOSE_INTERVAL_LONG, errorToast, infoToast, successToast, warnToast } from '@/utilities/toast.utilities';
import { formatApiError, generateTemporaryId, isStringSeries } from '@/utilities/utilities';
import { isPresentationWorkbookMode } from '@/utilities/workbookMode.utilities';
import { setOriginalView, setView } from '@/worksheet/worksheet.actions';
import { WORKSHEET_VIEW } from '@/worksheet/worksheet.constants';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqMetricsApi } from '@seeqdev/api/endpoint/MetricsApi';
import { sqTablesApi } from '@seeqdev/api/endpoint/TablesApi';
import { TypeEnum } from '@seeqdev/api/model/ColumnHeaderOutputV1';
import { ProcessTypeEnum, type ThresholdMetricOutputV1 } from '@seeqdev/api/model/ThresholdMetricOutputV1';
import { SeeqNames } from '@seeqdev/constants';
import type { MouseEvent } from 'react';
import type { TableColumnFilter } from '@/core/tableUtilities/tables.types';
import type { ScorecardMetric } from '@/tableBuilder/scorecard.types';
import type { ChartViewSettings, TableBuilderItem } from '@/tableBuilder/tableBuilder.store';
import type {
  AgGridChartMenuSettings,
  TableBuilderRowData,
  TreemapViewSettings,
} from '@/tableBuilder/tableBuilder.types';
import type { ConditionTableStat } from '@/tableDefinitionEditor/materializedTable.utilities';
import type { PropertyColumn, StatisticColumn } from '@/trendData/trendData.constants';
import type { CapsuleFormulaTableRow, ParametersMap } from '@/utilities/formula.constants';
import type { AnyProperty } from '@/utilities.types';
import type { TableSearchInputV1 } from '@seeqdev/api/model/TableSearchInputV1';
import type {
  ConditionTableStatInputForDirectItemsV1,
  PropertiesForItemUUIDColumn,
  TableFormula,
} from '@seeqdev/api/model/models';

/**
 * Adds multiple asset path columns to the simple table in a single batch.
 * This avoids race conditions when adding multiple levels simultaneously.
 *
 * NOTE: This function only dispatches column additions to the store.
 * The caller is responsible for fetching table data after all columns are added.
 */
export function addAssetPathColumns(assetPathId: string, assetPathLevels: number[]) {
  const existingLevels = new Set(
    sqTableBuilderStore.columns
      .filter((col) => col.type === TableBuilderColumnType.AssetPath)
      .map((col) => col.assetPathLevel),
  );

  assetPathLevels.forEach((assetPathLevel) => {
    if (existingLevels.has(assetPathLevel)) return;
    flux.dispatch('TABLE_BUILDER_ADD_COLUMN', {
      assetPathId,
      assetPathLevel,
      type: TableBuilderColumnType.AssetPath,
    });
  });
}

export function removeColumn(key: string) {
  if (_.has(_.find(sqTableBuilderStore.columns, { key }), 'filter')) {
    setColumnFilter(key, undefined);
  }
  flux.dispatch('TABLE_BUILDER_REMOVE_COLUMN', { key });
}

export function setColumnFilter(key: string, filter: TableColumnFilter | undefined) {
  flux.dispatch('TABLE_BUILDER_SET_COLUMN_FILTER', { key, filter });
}

export function setHeaderText(columnKey: string, text: string) {
  flux.dispatch('TABLE_BUILDER_SET_HEADER_TEXT', { columnKey, text });
}

export function setAssetPaths(assetPaths: any[]) {
  flux.dispatch('TABLE_BUILDER_SET_ASSET_PATHS', { assetPaths });
}

/** Removes all existing asset path columns. */
export function removeExistingAssetPathColumns() {
  sqTableBuilderStore.columns
    .filter((col) => col.key.startsWith(ASSET_PATH_COLUMN_PREFIX))
    .forEach((col) => removeColumn(col.key));
  setAssetPaths([]);
}

/** Adds asset path columns and configures their headers. */
export function addAssetPathColumnsWithHeaders(assetPaths: Array<{ id: string; value: number }>) {
  const addedColumns: Array<{ key: string; level: number }> = [];

  const assetPathsByIdMap = new Map<string, number[]>();
  assetPaths.forEach((assetPath) => {
    const levels = assetPathsByIdMap.get(assetPath.id) || [];
    levels.push(assetPath.value);
    assetPathsByIdMap.set(assetPath.id, levels);
  });

  assetPathsByIdMap.forEach((levels, assetPathId) => {
    addAssetPathColumns(assetPathId, levels);
  });

  assetPaths.forEach((assetPath) => {
    const column = sqTableBuilderStore.columns.find((col) => col.assetPathId === assetPath.id);
    if (!column) {
      return;
    }
    const headerText = i18next.t('TABLE_BUILDER.ASSET_PATH.COLUMN_HEADER', { level: assetPath.value });
    setHeaderText(column.key, headerText);
    addedColumns.push({ key: column.key, level: assetPath.value });
  });

  const currentAssetPaths = sqTableBuilderStore.assetPaths;
  const updatedAssetPaths = currentAssetPaths.map((path) => {
    const matchingAssetPath = assetPaths.find((ap) => ap.id === path.id);
    if (matchingAssetPath) {
      return {
        ...path,
        level: matchingAssetPath.value,
      };
    }
    return path;
  });
  setAssetPaths(updatedAssetPaths);

  return addedColumns;
}

/** Enables grouping on asset path columns in hierarchy order. */
export function enableAssetPathGrouping(columns: Array<{ key: string; level: number }>) {
  const sortedColumns = columns.slice().sort((a, b) => a.level - b.level);
  sortedColumns.forEach(({ key }) => {
    if (!key.startsWith(ASSET_PATH_ID_COLUMN_PREFIX)) {
      if (!sqTableBuilderStore.autoGroupColumn) {
        flux.dispatch('TABLE_BUILDER_TOGGLE_ROW_GROUPING');
      }
      flux.dispatch('TABLE_BUILDER_GROUP_BY_ROW', { key });
    }
  });
}

export const DATA_CANCELLATION_GROUP = 'tableBuilder';
export const HOMOGENIZE_UNIT_FORMULA_EXCEPTIONS = [
  'is not compatible with',
  'non-linear, cannot convert',
  'Rows with different units are not allowed',
];

const NAME_ITEM_PROPERTY = 'name';

/**
 * Sets the mode of the table builder
 *
 * @param mode - The mode
 */
export function setMode(mode: TableBuilderMode) {
  flux.dispatch('TABLE_BUILDER_SET_MODE', { mode });
  setItemStatusNotRequired();
  exposedForTesting.fetchTable();
}

/**
 * Adds a column to the table that the user can use to input free-form text.
 */
export function addTextColumn() {
  flux.dispatch('TABLE_BUILDER_ADD_COLUMN', {
    type: TableBuilderColumnType.Text,
  });
}

/**
 * Adds an ancestor column to the simple table
 *
 * @param assetPathId - The asset path id to identify which asset path is being added to the table
 * @param assetPathLevel - The level of the asset path
 */
export function addAssetPathColumn(assetPathId: string, assetPathLevel: number) {
  flux.dispatch('TABLE_BUILDER_ADD_COLUMN', {
    assetPathId,
    assetPathLevel,
    type: TableBuilderColumnType.AssetPath,
  });
  exposedForTesting.fetchTable();
}

/**
 * Adds an item property column to the table
 *
 * @param column - The property column to add
 * @param [isCapsuleProperty] - True if this is a capsule property, false if it is a property on an item.
 */
export function addPropertyColumn(column: { propertyName: string; style?: string }, isCapsuleProperty = false) {
  flux.dispatch('TABLE_BUILDER_ADD_COLUMN', {
    type: isCapsuleProperty ? TableBuilderColumnType.CapsuleProperty : TableBuilderColumnType.Property,
    style: column.style,
    propertyName: column.propertyName,
  });
  exposedForTesting.fetchTable();
}

/**
 * Adds or removes a particular column into the table builder store.
 *
 * @param column - The column being toggled. One of COLUMNS_AND_STATS
 * @param [signalId] - The series if it is a statistic column for condition table
 */
export function toggleColumn(column: PropertyColumn | StatisticColumn, signalId: string | null = null) {
  if (sqTableBuilderStore.isColumnEnabled(column, signalId!)) {
    exposedForTesting.removeColumn(sqTableBuilderStore.getColumnKey(column, signalId!));
    if (sqTreemapStore.getState().proportionByColumn === column.key) {
      sqTreemapStore.actions.setProportionByColumn(null);
    }
  } else {
    const uom = COLUMNS_AND_STATS.valueUnitOfMeasure;
    const disableUnitHomogenization =
      column.key === uom.key && !_.isUndefined(sqTableBuilderStore.assetId) && sqTableBuilderStore.isHomogenizeUnits;
    if (disableUnitHomogenization) {
      infoToast({
        messageKey: 'TABLE_BUILDER.UNIT_HOMOGENIZATION_DISABLED',
      });
      exposedForTesting.setHomogenizeUnits(false, false);
    }

    flux.dispatch('TABLE_BUILDER_ADD_COLUMN', { column, signalId });

    exposedForTesting.fetchTable();
  }
}

/**
 * Moves the specified column to a new position
 *
 * @param key - The key that identifies the column.
 * @param newKey - The key that identifies the column that will be the new position
 */
export function moveColumn(key: string, newKey: string) {
  flux.dispatch('TABLE_BUILDER_MOVE_COLUMN', { key, newKey });
}

export function addMetricColumn(metricItem: { id: string }) {
  flux.dispatch('TABLE_BUILDER_ADD_METRIC_TO_CONDITION_TABLE', {
    metricId: metricItem.id,
  });
}

export function isTableColumnEnabled(column: PropertyColumn | StatisticColumn, signalId: string | null = null) {
  return sqTableBuilderStore.isColumnEnabled(column, signalId);
}

export function statRowsIsSelected(data: TableBuilderRowData[]) {
  return sqTableBuilderStore.statRowsIsSelected(data);
}

/**
 * Sets the background color for a table column (header excluded).
 *
 * @param key - The key that identifies the column.
 * @param color - Background color for the column
 */
export function setColumnBackground(key: string | number, color: string) {
  flux.dispatch('TABLE_BUILDER_SET_COLUMN_BACKGROUND', { key, color });
}

/**
 * Sets a chart-only series color override for condition chart view.
 * This must not affect table column background styling.
 *
 * @param key - The key that identifies the condition row/series.
 * @param color - Series color for condition chart view.
 */
export function setConditionChartSeriesColor(key: string, color: string) {
  flux.dispatch('TABLE_BUILDER_SET_CONDITION_CHART_SERIES_COLOR', { key, color });
}

/**
 * Sets or clears the Treemap-only color override for a Simple Table condition.
 *
 * @param id - The condition id.
 * @param color - The override color, or undefined to clear the override.
 */
export function setSimpleTableTreemapConditionColor(id: string | number, color: string | undefined) {
  flux.dispatch('TABLE_BUILDER_SET_SIMPLE_TABLE_TREEMAP_CONDITION_COLOR', { id: String(id), color });
}

/**
 * Sets the text alignment for a table column (header excluded).
 *
 * @param key - The key that identifies the column.
 * @param align - CSS text-align value
 */
export function setColumnTextAlign(key: string, align: string) {
  flux.dispatch('TABLE_BUILDER_SET_COLUMN_TEXT_ALIGN', { key, align });
}

/**
 * Sets the text color for a table column (header excluded).
 *
 * @param key - The key that identifies the column.
 * @param color - Text color
 */
export function setColumnTextColor(key: string | number, color: string) {
  flux.dispatch('TABLE_BUILDER_SET_COLUMN_TEXT_COLOR', { key, color });
}

/**
 * Sets the text style for a table column (header excluded).
 *
 * @param key - The key that identifies the column.
 * @param style - zero or more text style attributes
 */
export function setColumnTextStyle(key: string, style: string[]) {
  flux.dispatch('TABLE_BUILDER_SET_COLUMN_TEXT_STYLE', { key, style });
}

/**
 * Sets the background color for a table header.
 *
 * @param key - The key that identifies the column.
 * @param color - Background color for the header
 */
export function setHeaderBackground(key: string | number, color: string) {
  flux.dispatch('TABLE_BUILDER_SET_HEADER_BACKGROUND', { key, color });
}

/**
 * Sets the text alignment for a table header.
 *
 * @param key - The key that identifies the column.
 * @param align - CSS text-align value
 */
export function setHeaderTextAlign(key: string, align: string) {
  flux.dispatch('TABLE_BUILDER_SET_HEADER_TEXT_ALIGN', { key, align });
}

/**
 * Sets the text color for a table header.
 *
 * @param key - The key that identifies the column.
 * @param color - Text color
 */
export function setHeaderTextColor(key: string | number, color: string) {
  flux.dispatch('TABLE_BUILDER_SET_HEADER_TEXT_COLOR', { key, color });
}

/**
 * Sets the text style for a table header.
 *
 * @param key - The key that identifies the column.
 * @param style - zero or more text style attributes
 */
export function setHeaderTextStyle(key: string, style: string[]) {
  flux.dispatch('TABLE_BUILDER_SET_HEADER_TEXT_STYLE', { key, style });
}

/**
 * Applies the formatting of the specified column to all columns (headers excluded)
 * @param key - The key that identifies the column.
 */
export function setStyleToAllColumns(key: string) {
  flux.dispatch('TABLE_BUILDER_SET_STYLE_TO_ALL_COLUMNS', { key });
}

/**
 * Applies the formatting of the specified column to all headers
 * @param key - The key that identifies the column.
 */
export function setStyleToAllHeaders(key: string) {
  flux.dispatch('TABLE_BUILDER_SET_STYLE_TO_ALL_HEADERS', { key });
}

/**
 * Applies the formatting of the specified column&header to all columns and headers
 * @param key - The key that identifies the column.
 */
export function setStyleToAllHeadersAndColumns(key: string) {
  flux.dispatch('TABLE_BUILDER_SET_STYLE_TO_ALL_HEADERS_AND_COLUMNS', {
    key,
  });
}

/**
 * Copies the formatting of the specified column&header.
 * @param key - The key that identifies the column.
 */
export function copyStyle(key: string) {
  flux.dispatch('TABLE_BUILDER_COPY_STYLE', { key });
}

/**
 * Applies the copied formatting to the specified column header
 * @param key - The key that identifies the column.
 */
export function pasteStyleOnHeader(key: string) {
  flux.dispatch('TABLE_BUILDER_PASTE_STYLE_ON_HEADER', { key });
}

/**
 * Applies the copied formatting to the specified column (header excluded)
 * @param key - The key that identifies the column.
 */
export function pasteStyleOnColumn(key: string) {
  flux.dispatch('TABLE_BUILDER_PASTE_STYLE_ON_COLUMN', { key });
}

/**
 * Applies the copied formatting to the specified column&header
 * @param key - The key that identifies the column.
 */
export function pasteStyleOnHeaderAndColumn(key: string) {
  flux.dispatch('TABLE_BUILDER_PASTE_STYLE_ON_HEADER_AND_COLUMN', { key });
}

export function sortByColumn(key: string, direction: string) {
  flux.dispatch('TABLE_BUILDER_SORT_BY_COLUMN', { key, direction });
}

/**
 * Sets the text for a scorecard column cell or header.
 *
 * @param key - The key that identifies the column.
 * @param text - Text for the cell
 * @param [cellKey] - The identifier for the cell. If not specified the column header text will be set.
 */
export function setCellText(key: string, text: string, cellKey?: string) {
  flux.dispatch('TABLE_BUILDER_SET_CELL_TEXT', { key, text, cellKey });
}

/**
 * Sets the header override flag for a column. Other column overrides will be disabled.
 *
 * @param columnKey - The key that identifies the column.
 */
export function setHeaderOverridden(columnKey: string) {
  flux.dispatch('TABLE_BUILDER_SET_HEADER_OVERRIDE', { columnKey });
}

/**
 * Sets the header type for either scorecard columns that display the metric values or the name column for simple
 * tables.
 *
 * @param type - The type of header to display
 */
export function setHeadersType(type: TableBuilderHeaderType) {
  flux.dispatch('TABLE_BUILDER_SET_HEADERS_TYPE', { type });
  if (type === TableBuilderHeaderType.CapsuleProperty) {
    exposedForTesting.fetchTable();
  }
}

/**
 * Sets the date format used for headers of metric value columns/name column when the type is one of the date types.
 *
 * @param format - A string that can be passed to moment's format()
 */
export function setHeadersFormat(format: string) {
  flux.dispatch('TABLE_BUILDER_SET_HEADERS_FORMAT', { format });
}

/**
 * Sets the name of the capsule property used for headers of metric value columns when the type is CapsuleProperty.
 *
 * @param property - The capsule property name
 */
export function setHeadersProperty(property: string) {
  flux.dispatch('TABLE_BUILDER_SET_HEADERS_PROPERTY', { property });
  exposedForTesting.fetchTable();
}

export function setIsTransposed(isTransposed: boolean) {
  flux.dispatch('TABLE_BUILDER_SET_IS_TRANSPOSED', { isTransposed });
}

/**
 * Sets or clears the asset id so the table can be run across all the child assets of the asset.
 *
 * @param assetId - The root asset to run the formula across or undefined to clear.
 */
export function setAssetId(assetId: string | undefined) {
  flux.dispatch('TABLE_BUILDER_SET_ASSET_ID', { assetId });
  if (assetId) {
    flux.dispatch('TABLE_BUILDER_ADD_COLUMN', { column: { key: 'asset' } });
  } else {
    flux.dispatch('TABLE_BUILDER_REMOVE_COLUMN', { key: 'asset' });
  }

  exposedForTesting.fetchTable();
}

/**
 * Sets the homogenizeUnits attribute and removes the UOM column when the units are homogenized. In this case, we do
 * not want to show the UOM column. The unit of some values might be different than the unit of the item
 * @param homogenizeUnits - The homogenize units value
 * @param fetchTable - When true, it fetches the table
 * @return void if only homogenizeUnits is set. When fetch table is needed it returns a promise resolves when the
 * table has been been fetched
 */
export function setHomogenizeUnits(homogenizeUnits: boolean, fetchTable = false): void | Promise<void | any[]> {
  flux.dispatch('TABLE_BUILDER_SET_HOMOGENIZE_UNITS', { homogenizeUnits });
  if (
    homogenizeUnits &&
    sqTableBuilderStore.isSimpleMode() &&
    sqTableBuilderStore.isColumnEnabled(COLUMNS_AND_STATS.valueUnitOfMeasure)
  ) {
    const key = COLUMNS_AND_STATS.valueUnitOfMeasure.key;
    flux.dispatch('TABLE_BUILDER_REMOVE_COLUMN', { key });
    infoToast({
      messageKey: 'TABLE_BUILDER.UNIT_COLUMN_REMOVED',
    });
  }

  if (fetchTable) {
    return exposedForTesting.fetchTable();
  }
}

/**
 * Changes to the specified the asset id only if a current asset is already set and the new one is different.
 *
 * @param assetId - The asset to change to
 */
export function changeAssetId(assetId: string) {
  if (sqTableBuilderStore.assetId && sqTableBuilderStore.assetId !== assetId) {
    exposedForTesting.setAssetId(assetId);
  }
}

export function setIsMigrating(isMigrating: boolean) {
  flux.dispatch('TABLE_BUILDER_SET_IS_MIGRATING', { isMigrating });
}

export function setIsTableStriped(isTableStriped: boolean) {
  flux.dispatch('TABLE_BUILDER_SET_IS_TABLE_STRIPED', { isTableStriped });
}

export function setShowChartView(showChart: boolean) {
  flux.dispatch('TABLE_BUILDER_SET_CHART_VIEW', { enabled: showChart });
}

export function setShowConditionChartView(showChart: boolean) {
  flux.dispatch('TABLE_BUILDER_SET_CONDITION_CHART_VIEW', { conditionEnabled: showChart });
}

export function setTreemapView(showTreemap: boolean) {
  flux.dispatch('TABLE_BUILDER_SET_TREEMAP_VIEW', { enabled: showTreemap });
}

/**
 * True/false toggle gives user a choice between "Signal colors" and "Seeq colors" for the Highcharts chart
 * in the Tables & Charts section of Workbench Analysis. When true, the colors selected in the Details
 * pane are used, unless something overrides the choice (such as being in Transposed mode).
 * @param useSignalColorsInChart - When true, uses the signal colors defined in the Details pane below the chart
 * @return void
 */
export function setUseSignalColorsInChart(useSignalColorsInChart: boolean) {
  flux.dispatch('TABLE_BUILDER_SET_USE_SIGNAL_COLORS_IN_CHART', { useSignalColorsInChart });
}

export function setChartViewSettings(settings: ChartViewSettings | undefined) {
  flux.dispatch('TABLE_BUILDER_SET_CHART_VIEW_SETTINGS', {
    settings: { ...settings },
  });
}

export function setChartMenuSettings(chartMenuSettings: AgGridChartMenuSettings) {
  flux.dispatch('TABLE_BUILDER_SET_CHART_MENU_SETTINGS', chartMenuSettings);
}

export function setChartViewConditionSettings(settings: ChartViewSettings | undefined) {
  flux.dispatch('TABLE_BUILDER_SET_CHART_VIEW_CONDITION_SETTINGS', {
    conditionSettings: settings,
  });
}

export function setTreemapViewSettings(settings: TreemapViewSettings | undefined) {
  flux.dispatch('TABLE_BUILDER_SET_TREEMAP_VIEW_SETTINGS', {
    settings,
  });
}

/**
 * Enables or disables formula-across mode for Table & Charts (the "Scale" toolbar toggle).
 *
 * When enabled, ensures the `asset` column is present; when disabled, removes it.
 * Triggers a table fetch so the displayed data reflects the new mode.
 *
 * @param isFormulaAcrossTable - True to enable Scale/formula-across mode, false to disable it.
 */
export async function setIsFormulaAcrossTable(isFormulaAcrossTable: boolean) {
  const items = sqTableBuilderStore.getTableItems();
  const shouldLockScale = isScaleLockedByTreemap(
    sqTableBuilderStore.isSimpleMode(),
    sqTableBuilderStore.showTreemapView,
    items,
  );

  if (!isFormulaAcrossTable && shouldLockScale) {
    return;
  }

  flux.dispatch('TABLE_BUILDER_SET_IS_FORMULA_ACROSS_TABLE', {
    isFormulaAcrossTable,
  });

  if (isFormulaAcrossTable) {
    flux.dispatch('TABLE_BUILDER_ADD_COLUMN', { column: { key: 'asset' } });
  } else {
    flux.dispatch('TABLE_BUILDER_REMOVE_COLUMN', { key: 'asset' });
  }
  return await exposedForTesting.fetchTable();
}

/**
 * Remove the v1 metric.
 *
 * @param {string} metricId - The id of the metric
 */
export function removeOldMetric(metricId: string) {
  sqScorecardStore.actions.removeMetric(metricId);
}

/**
 * Fetches and dispatches the table data.
 *
 * @param includeAncestors - When true, simple formula-across table queries request ancestor columns from the API.
 * @param enableGrouping - When true with includeAncestors, enables row grouping on asset path columns added from the response.
 * @return {Promise} A promise that resolves when the table has been fetched
 */
export function fetchTable(includeAncestors?: boolean, enableGrouping?: boolean): Promise<void | any[]> {
  const afterFetch = () => {
    flux.dispatch('TABLE_BUILDER_SET_TABLE_FETCH_ATTEMPTED');
  };
  if (sqWorksheetStore.view.key !== WORKSHEET_VIEW.TABLE) {
    return Promise.resolve().finally(afterFetch);
  }
  return cancelGroup(DATA_CANCELLATION_GROUP, true).then(() => {
    const [scalingItems] = getScalingAndRegularTableItems();

    let promise: Promise<void | any[]>;
    if (sqScorecardStore.getState().metrics.length) {
      promise = exposedForTesting.fetchOldMetrics();
    } else if (sqTableBuilderStore.isSimpleMode()) {
      if (scalingItems.length > 0) {
        const hasAssetPathColumns = sqTableBuilderStore.columns.some(
          (col) => col.type === TableBuilderColumnType.AssetPath,
        );
        promise = exposedForTesting.fetchFormulaAcrossSimpleTableData(
          includeAncestors ?? (sqTableBuilderStore.showTreemapView || hasAssetPathColumns),
          enableGrouping ?? (sqTableBuilderStore.showTreemapView || hasAssetPathColumns),
        );
      } else {
        promise = exposedForTesting.fetchSimpleTableData();
      }
    } else {
      if (scalingItems.length > 0) {
        promise = exposedForTesting.fetchFormulaAcrossConditionTableData();
      } else {
        promise = exposedForTesting.fetchConditionTableData();
      }
    }
    return promise.finally(afterFetch);
  });
}

/**
 * Fetches and dispatches the simple table data.
 * Because the calc engine requires that the type information be the same for all rows, each type of item in the
 * details pane is fetched separately and then combined into a single table for the simple table.
 * @return A promise that resolves when the table has been fetched
 */
export async function fetchSimpleTableData(): Promise<void | any[]> {
  const items = sqTableBuilderStore.getTableItemsProcess();

  const isCondition = (item: { itemType: ITEM_TYPES }) => item.itemType === ITEM_TYPES.CONDITION;
  const isSignal = (item: { itemType: ITEM_TYPES }) => item.itemType === ITEM_TYPES.SERIES;
  const isScalar = (item: { itemType: ITEM_TYPES }) => item.itemType === ITEM_TYPES.SCALAR;
  const stringItems = _.filter(items, (item) => isStringSeries(item) && isSignal(item));
  const numericItems = _.reject(
    items,
    (item) => isCondition(item) || isScalar(item) || isStringSeries(item) || !isSignal(item),
  );
  const conditionItems = _.filter(items, (item) => isCondition(item));
  const scalarItems = _.filter(items, (item) => isScalar(item));

  const allMetricItems = getAllItems({ workingSelection: false, itemTypes: [ITEM_TYPES.METRIC] });
  const metricMap = sqTableBuilderStore.findNonConflictingSimpleMetrics(items.map((it) => it.id));

  const selectedMetricItems = _.reject(items, (item) => isCondition(item) || isSignal(item) || isScalar(item));
  // All the metrics where a given selected item is tied to just that metric and no other metrics, and that metric
  // is not included in the selected items
  const extraMetricsToFetch = allMetricItems
    ? allMetricItems.filter((metric) => {
        // Get all items that have this metric in their mapping
        const itemsWithMetric = items.filter((item) => {
          const mapping = metricMap[item.id];
          return mapping && _.values(mapping).includes(metric.id);
        });

        // Only include the metric if it's not already in selectedMetricItems
        // and there are items that have this metric in their mapping
        return (
          !selectedMetricItems.find((selectedMetric) => selectedMetric.id === metric.id) && itemsWithMetric.length > 0
        );
      })
    : [];

  const metricItems = selectedMetricItems.concat(extraMetricsToFetch);

  const numericSignalParameters = sqTableBuilderStore.getSimpleTableFetchParams(numericItems);
  const stringSignalParameters = sqTableBuilderStore.getSimpleTableFetchParams(stringItems);
  const conditionParameters = sqTableBuilderStore.getSimpleTableFetchParams(conditionItems);
  const scalarParameters = sqTableBuilderStore.getSimpleTableFetchParams(scalarItems);
  const metricParameters = sqTableBuilderStore.getSimpleTableFetchParams(metricItems);

  if (
    _.isEmpty(numericSignalParameters.formula) &&
    _.isEmpty(stringSignalParameters.formula) &&
    _.isEmpty(conditionParameters.formula) &&
    _.isEmpty(scalarParameters.formula) &&
    _.isEmpty(metricParameters.formula)
  ) {
    return Promise.resolve();
  }

  const itemIds = _.chain([numericItems, stringItems, conditionItems, scalarItems, metricItems])
    .flatMap()
    .map('id')
    .filter(validateGuid)
    .value();

  _.forEach(itemIds, (id) => {
    setDataStatusLoadingInTrendStores(id);
  });
  try {
    const computeTableIfNeeded = async (
      items: Array<{ id: string | number }>,
      parameters: { formula: string; parameters: ParametersMap; root: string | undefined },
    ) => {
      if (items.length > 0) {
        return await computeTable({
          formula: parameters.formula,
          parameters: parameters.parameters,
          root: parameters.root,
          cancellationGroup: DATA_CANCELLATION_GROUP,
          usePost: true,
          itemsToScaleAcross: items.map((item) => String(item.id)),
        });
      }
      return Promise.resolve();
    };

    const [numericTable, stringTable, conditionTable, scalarTable, metricTable] = await Promise.all([
      computeTableIfNeeded(numericItems, numericSignalParameters),
      computeTableIfNeeded(stringItems, stringSignalParameters),
      computeTableIfNeeded(conditionItems, conditionParameters),
      computeTableIfNeeded(scalarItems, scalarParameters),
      computeTableIfNeeded(metricItems, metricParameters),
    ]);

    flux.dispatch(
      'TABLE_BUILDER_PUSH_SIMPLE_DATA',
      {
        tableAndColumnPositions: [
          [numericTable, numericSignalParameters.columnPositions],
          [stringTable, stringSignalParameters.columnPositions],
          [conditionTable, conditionParameters.columnPositions],
          [scalarTable, scalarParameters.columnPositions],
          [metricTable, metricParameters.columnPositions],
        ],
        idsToFilterOut: extraMetricsToFetch.map((item) => item.id),
        items: items.concat(extraMetricsToFetch),
      },
      PUSH_IGNORE,
    );

    _.forEach(
      items.concat(extraMetricsToFetch).map((item) => item.id),
      (id) => {
        const tableIndex = _.findIndex(
          [numericItems, stringItems, conditionItems, scalarItems, metricItems, extraMetricsToFetch],
          (itemsArray) => _.some(itemsArray, (item) => item.id === id),
        );
        const tables = [numericTable, stringTable, conditionTable, scalarTable, metricTable];

        const correctWarningLogs = getCorrectWarningLogs(id, tables[tableIndex].warningLogs);

        setDataStatusPresentInTrendStores({
          id,
          warningCount: tables[tableIndex].warningCount,
          warningLogs: correctWarningLogs,
          timingInformation: tables[tableIndex].timingInformation,
          meterInformation: tables[tableIndex].meterInformation,
          isSharedRequest: true,
        });
      },
    );
    // not waiting for a promise - fetches options for the dropdown in the filter
    exposedForTesting.fetchSimpleTableDistinctStringValues();
  } catch (e) {
    return exposedForTesting.catchTableBuilderDataFailure(
      e,
      TableBuilderMode.Simple,
      sqTableBuilderStore.getTableItems(),
    );
  }
}

/** Parses warnings from batch computations (FormulaAcrossItems), ensuring only relevant items retain warningLogs.
 *  Cleans up messages to remove Ids.
 */
function getCorrectWarningLogs(id: string, warningLogs: any[]) {
  if (!warningLogs || warningLogs.length === 0) {
    return [];
  }

  const warningMessagesWithIds = warningLogs.flatMap(
    (warningLog) =>
      warningLog.formulaLogEntries?.SWAP_ERROR?.logDetails?.map((detail: { message: any }) => detail.message) ?? [],
  );

  const allItemIdsWithWarnings = warningMessagesWithIds.flatMap((itemIdString) => extractGuids(itemIdString));

  if (!allItemIdsWithWarnings.includes(id?.toString().toLowerCase())) {
    return allItemIdsWithWarnings.length > 0 ? [] : warningLogs;
  }

  return warningLogs.map((log) => ({
    ...log,
    formulaLogEntries: {
      ...log.formulaLogEntries,
      SWAP_ERROR: {
        ...log.formulaLogEntries?.SWAP_ERROR,
        logDetails: log.formulaLogEntries?.SWAP_ERROR?.logDetails?.map((detail: { message: string }) => ({
          ...detail,
          message: extractGuids(detail.message).reduce((memo, guid) => memo.replace(`[${guid}]`, ''), detail.message),
        })),
      },
    },
  }));
}

/**
 * Fetch the distinct string values for each string-valued column in the Simple Table.
 * Noops in presentation mode since the filters can't be changed.
 */
export function fetchSimpleTableDistinctStringValues(): Promise<void> {
  if (isPresentationWorkbookMode()) {
    return Promise.resolve();
  }

  const { fetchParamsList, columnKeysNamesList } = sqTableBuilderStore.getSimpleTableStringColumnsFetchParams();
  return Promise.all(
    _.map(fetchParamsList, (tableFetchParam) =>
      computeTable({
        formula: tableFetchParam.formula,
        parameters: tableFetchParam.parameters,
        reduceFormula: tableFetchParam.reduceFormula,
        root: tableFetchParam.root,
        cancellationGroup: DATA_CANCELLATION_GROUP,
        usePost: true, // Formula can be very long
      }),
    ),
  ).then((stringValueTables) => {
    flux.dispatch(
      'TABLE_BUILDER_SET_SIMPLE_DISTINCT_STRING_VALUES',
      {
        stringValueTables,
        columnKeysNamesList,
      },
      PUSH_IGNORE,
    );
  });
}

/**
 * Fetches and dispatches the condition table data.
 *
 * @return A promise that resolves when the table has been fetched
 */
export function fetchConditionTableData(): Promise<void | unknown[]> {
  return workbenchTextContextEnrichment()
    ? exposedForTesting.fetchConditionTableDataViaConditionApi()
    : exposedForTesting.fetchConditionTableDataViaComputeCapsuleTable();
}

type ConditionTablePresentMetadata = {
  warningCount?: number;
  warningLogs?: unknown[];
  // Sourced from the server-timing/server-meters response headers
  timingInformation?: string;
  meterInformation?: string;
};

/**
 * The table request captures its item ids before it starts, but an item can be found to be redacted (a 403 from its
 * own fetch) while the request is in flight. Writing LOADING or PRESENT for it would erase that, and the item would
 * then render as readable, so redacted items keep their status.
 */
const withoutRedactedItems = (itemIds: string[]): string[] => itemIds.filter((id) => !isItemRedacted({ id }));

const dispatchConditionTableLoading = (itemIds: string[]): void => {
  withoutRedactedItems(itemIds).forEach((id) => {
    setDataStatusLoadingInTrendStores(id);
  });
};

const dispatchConditionTablePresent = (itemIds: string[], metadata: ConditionTablePresentMetadata): void => {
  withoutRedactedItems(itemIds).forEach((id) => {
    setDataStatusPresentInTrendStores({
      id,
      warningCount: metadata.warningCount,
      warningLogs: metadata.warningLogs,
      timingInformation: metadata.timingInformation,
      meterInformation: metadata.meterInformation,
      isSharedRequest: true,
    });
  });
};

const dispatchConditionTableData = <T>(payload: T): void => {
  flux.dispatch('TABLE_BUILDER_PUSH_CONDITION_DATA', payload, PUSH_IGNORE);
};

/**
 * Fetches and dispatches condition table data using the legacy formula/compute path.
 * This path is intentionally kept intact for flag-off parity.
 *
 * @return A promise that resolves when the table has been fetched
 */
export const fetchConditionTableDataViaComputeCapsuleTable = (): Promise<void | unknown[]> => {
  const {
    ids: itemIds,
    assetId,
    propertyColumns,
    statColumns,
    customPropertyName,
    buildAdditionalFormula,
    itemColumnsMap,
    buildConditionFormula,
    buildStatFormula,
  } = sqTableBuilderStore.getConditionTableFetchParams();

  if (_.isEmpty(itemIds)) {
    return Promise.resolve();
  }

  dispatchConditionTableLoading(itemIds);

  return computeCapsuleTable({
    columns: { propertyColumns, statColumns },
    range: { start: sqDurationStore.getState().displayRange.start, end: sqDurationStore.getState().displayRange.end },
    itemIds,
    buildConditionFormula,
    root: assetId,
    buildAdditionalFormula,
    buildStatFormula,
    offset: 0,
    limit: MAX_CONDITION_TABLE_CAPSULES,
    cancellationGroup: DATA_CANCELLATION_GROUP,
  })
    .then(({ data: { headers, table, timingInformation, meterInformation, warningCount, warningLogs } }) => {
      dispatchConditionTableData({ headers, table, itemColumnsMap, customPropertyName });
      dispatchConditionTablePresent(itemIds, {
        warningCount,
        warningLogs,
        timingInformation,
        meterInformation,
      });
    })
    .catch((e) =>
      exposedForTesting.catchTableBuilderDataFailure(
        e,
        TableBuilderMode.Condition,
        sqTableBuilderStore.getTableItems(),
      ),
    );
};

/**
 * Fetches and dispatches condition table data using `POST /api/tables/condition`.
 * This path is enabled only by `fetchConditionTableData` when text-context enrichment is on.
 *
 * @return A promise that resolves when the table has been fetched
 */
export const fetchConditionTableDataViaConditionApi = async (): Promise<void | unknown[]> => {
  const {
    ids: itemIds,
    assetId,
    statColumns,
    customPropertyName,
    itemColumnsMap,
  } = sqTableBuilderStore.getConditionTableFetchParams();

  if (_.isEmpty(itemIds)) {
    return;
  }

  const start = getDurationDisplayRange().start.toISOString();
  const end = getDurationDisplayRange().end.toISOString();
  const conditionStats: ConditionTableStatInputForDirectItemsV1[] = statColumns.map((column) => ({
    statistic: column.stat,
    itemId: column.signalId,
  }));
  const statItems = conditionStats.map(({ itemId }) => itemId);
  const { capsuleProperties, conditionProperties } = buildConditionTableColumnRequestFields(
    sqTableBuilderStore.columns,
  );
  const visibleColumnKeys = new Set(sqTableBuilderStore.columns.map((column) => column.key));

  if (
    customPropertyName &&
    !capsuleProperties.includes(customPropertyName) &&
    !visibleColumnKeys.has(customPropertyName)
  ) {
    capsuleProperties.push(customPropertyName);
  }

  const includeDirectAssetColumn = visibleColumnKeys.has(COLUMNS_AND_STATS.asset.key);
  const includeFullPathColumn = visibleColumnKeys.has(COLUMNS_AND_STATS.fullpath.key);

  dispatchConditionTableLoading(itemIds);

  try {
    const { data: response } = await sqTablesApi.queryConditionTable(
      {
        conditionSource: {
          directItemIdsSource: {
            itemIds,
            assetId,
            conditionStats,
          },
        },
        start: { value: start },
        end: { value: end },
        conditionProperties,
        capsuleProperties,
        includeDirectAssetColumn,
        includeFullPathColumn,
        limit: MAX_CONDITION_TABLE_CAPSULES,
      },
      { cancellationGroup: DATA_CANCELLATION_GROUP },
    );

    const { headers, structuredTable } = normalizeConditionTableResponse(
      response,
      statItems,
      {
        columns: sqTableBuilderStore.columns,
        conditionTableRows: sqTableBuilderStore.conditionTableColumns.rows,
        headers: sqTableBuilderStore.headers,
      },
      { mergeRowsByCapsule: true },
    );

    dispatchConditionTableData({ headers, table: structuredTable, itemColumnsMap, customPropertyName });
    dispatchConditionTablePresent(itemIds, {});
  } catch (e) {
    return exposedForTesting.catchTableBuilderDataFailure(
      e,
      TableBuilderMode.Condition,
      sqTableBuilderStore.getTableItems(),
    );
  }
};

/**
 * Performs all necessary steps to execute all v1 metrics using the current display range.
 *
 * @return Promise that resolves when the cell is computed
 */
export function fetchOldMetrics(): Promise<void> {
  return (
    _.chain(sqScorecardStore.getState().metrics)
      .map((metric: ScorecardMetric) => {
        const statFragment = getStatisticFragment(metric.stat!);
        const formula = `$series.aggregate(${statFragment}, ${getCapsuleFormula(getDurationDisplayRange())})`;

        return computeScalar({ formula, parameters: { series: metric.itemId } }).then((result) => {
          const payload = _.assign(
            {
              metricId: metric.metricId,
              valueResult: `${formatNumber(result.value)} ${result.uom}`,
            },
            exposedForTesting.computeColorForOldMetric(metric, result.value),
          );

          sqScorecardStore.actions.setValueResult(payload);
        });
      })
      .thru((promises) => Promise.all(promises))
      .value()
      // Noop at the end so we have a void return type
      .then(() => {})
  );
}

/**
 * Compute the color for a given value. This finds the maximum threshold value that is less or equal to the value,
 * and returns that. It also computes the contrasting color for the foreground.
 *
 * @param {Object} metric - The metric object
 * @param {Object} metric.thresholds - Array of threshold objects
 * @param {Number} value - The value to choose colors for
 * @returns {{backgroundColor: string, foregroundColor: string}} - Map of background and foreground colors
 */
export function computeColorForOldMetric(metric: ScorecardMetric, value: number) {
  const color = (
    _.chain(metric.thresholds)
      .filter(function (t: any) {
        return !_.isUndefined(t.isMinimum) || t.threshold <= value;
      })
      .head() as any
  )
    .get('color', '#ddd')
    .value();
  return {
    backgroundColor: color,
    foregroundColor: tinycolor(color).isDark() ? '#fff' : '#000',
  };
}

/**
 * Displays a metric on the trend with a specific time region of the chart highlighted. It also takes care of
 * swapping to the new asset if the table is going across assets and the row that the user is clicking on is from a
 * different asset than the one currently being shown.
 *
 * @param metricId - The metric identifier
 * @param itemId - The id of the actual item in the row. This can be different from the metricId when swapping
 * @param start - The start time of the window to highlight
 * @param end - The end time of the window to highlight
 * @param event - The click event
 */
export function displayMetricOnTrend(
  metricId: string,
  itemId: string,
  start: number,
  end: number,
  event?: MouseEvent,
): Promise<void> {
  const view = event?.view as Window | null | undefined;
  if ((view?.getSelection?.()?.toString().length ?? 0) > 0) {
    return Promise.resolve(); // noop if the user is selecting the value
  }

  let metric = _.find(
    getAllItems({
      itemTypes: [ITEM_TYPES.METRIC],
    }),
    { id: metricId },
  );
  const isItemPresent = _.some(getAllItems({}), { id: itemId });
  let promise;
  if (!sqTableBuilderStore.assetId || isItemPresent) {
    promise = Promise.resolve();
  } else {
    // User clicked on a metric whose item is not in the details pane, so swap to its parent
    promise = getDependencies({ id: itemId }).then(({ assets }) => {
      if (assets.length && assets[0].pathComponentIds.length) {
        swapAsset({ id: _.last(assets[0].pathComponentIds)! }).then(() => {
          // Actual item clicked should now be in the details pane
          metric = _.find(getAllItems({ itemTypes: [ITEM_TYPES.METRIC] }), { id: itemId });
        });
      }
    });
  }

  return promise.then(() => {
    setOriginalView(WORKSHEET_VIEW.TABLE);
    setView(WORKSHEET_VIEW.TREND);

    const isSimpleMetric = _.get(metric, 'definition.processType') === ProcessTypeEnum.Simple;
    const isBatchMetric = _.get(metric, 'definition.processType') === ProcessTypeEnum.Condition;
    const boundingCondition = _.get(metric, 'definition.boundingCondition', {});
    if (isBatchMetric && boundingCondition.id) {
      addTrendItem(boundingCondition).then((bc) => setItemSelected(bc, true));
    }

    _.forEach(getAllItems({}), (item) => setItemSelected(item, item.id === metric.id));

    if (isSimpleMetric) {
      addTrendItem(metric.definition.measuredItem).then(() => alignMeasuredItemWithMetric(metric));
    }

    if (
      sqDurationStore.getState().displayRange.start !== start ||
      sqDurationStore.getState().displayRange.end !== end
    ) {
      setTrendSelectedRegion(start, end);
    }

    if (!sqTrendStore.hideUnselectedItems) {
      toggleHideUnselectedItems();
    }
  });
}

/**
 * Migrates old scorecard to be backend threshold metric items.
 */
export function migrate() {
  exposedForTesting.setIsMigrating(true);
  exposedForTesting.setMode(TableBuilderMode.Simple);
  exposedForTesting.setHeadersType(TableBuilderHeaderType.None);
  exposedForTesting.removeColumn(COLUMNS_AND_STATS['statistics.average'].key);
  exposedForTesting.toggleColumn(COLUMNS_AND_STATS.metricValue);
  _.chain(sqScorecardStore.getState().metrics)
    .map((metric: any) =>
      exposedForTesting
        .getName(metric)
        .then((name) =>
          sqMetricsApi.createThresholdMetric({
            name,
            measuredItem: metric.itemId,
            aggregationFunction: getStatisticFragment(metric.stat),
            thresholds: exposedForTesting.getThresholds(metric),
          }),
        )
        .then(({ data: item }) => {
          exposedForTesting.removeOldMetric(metric.metricId);
          return item;
        })
        .catch((error) => {
          errorToast({ httpResponseOrError: error });
        }),
    )
    .thru((promises) => Promise.all(promises))
    .value()
    // Important that they are added in order to preserve the sort
    .then((items) =>
      Promise.all(
        _.map(items, (item) => {
          const promise = addTrendItem(item);
          setItemSelected(item as ThresholdMetricOutputV1, true);
          return promise;
        }),
      ),
    )
    .finally(() => {
      exposedForTesting.setIsMigrating(false);
      successToast(
        {
          messageKey: 'TABLE_BUILDER.MIGRATION_SUCCESS',
        },
        { autoClose: AUTO_CLOSE_INTERVAL_LONG },
      );
    });
}

/**
 * Returns a name for the metric. Specifically handles the case of metric that had an empty name. It is not
 * guaranteed to be unique. Instead, since the backend does not enforce uniqueness, it assumes the user will figure
 * out the best way to disambiguate duplicate names when they edit it.
 *
 * @param {Object} metric - The metric
 * @return {Promise<String>} - Promise that resolves with a name for the metric
 */
export function getName(metric: ScorecardMetric) {
  const trimmedName = metric.name?.trim() ?? '';

  return Promise.resolve(trimmedName)
    .then((name) => {
      if (!name) {
        return sqItemsApi.getItemAndAllProperties({ id: metric.itemId }).then(({ data: item }) => {
          const method = SAMPLE_FROM_SCALARS.VALUE_METHODS.find((m) => m.key === metric.stat?.key);
          const statTitle = method?.title ? i18next.t(method.title) : '';

          return `${statTitle} ${item.name}`;
        });
      }

      return name;
    })
    .then((name) => getDefaultName(name, sqWorkbenchStore.stateParams.workbookId))
    .then((defaultName) => {
      // getDefaultName name will add a suffix, but if that suffix is 1 the name is unique so we don't need the
      // number
      if (defaultName.endsWith(' 1')) {
        return defaultName.slice(0, -2);
      }

      return defaultName;
    });
}

/**
 * Gets the thresholds for a metric, mapped to the new priority levels. Makes no assumptions that the colors are
 * the same, but instead makes the assumption that the user ordered their metrics with the same priority order as
 * the order presented by METRIC_COLORS. It has the following known limitations:
 *  - It can assign the same level to two different thresholds if there is no corresponding new priority. This
 *  could happen, for example, if user used both the green and blue as thresholds, since blue was removed in the
 *  new scorecard.
 *  - If the thresholds have the colors in a random order, that order will not be preserved since the order cannot
 *  be changed for the new priorities.
 *  - If the user defined more thresholds than the number of new priorities then some of the old thresholds will
 *  be lost.
 *
 * @param {Object} metric - The metric
 * @return {String[]} Array of thresholds in the format of priorityLevel=value
 */
export function getThresholds(metric: ScorecardMetric): string[] {
  const highPriorities = _.filter(priorityColors(), (priority) => priority.level > 0);
  const lowercaseMetricColors = _.map(METRIC_COLORS, _.toLower);
  const priorityConversionMap = _.chain(lowercaseMetricColors)
    .initial() // discard the last color (white) since neutral is not included in priorities
    .reverse() // reverse it so that the indices correspond with the priority levels
    .transform(
      (result, color, i) => {
        // Use the index to find the corresponding priority. Green's index is zero and with this algorithm that
        // means it ends up as the neutral priority, but that is ok since R21 moved green to a neutral color. The
        // rest of the colors will then map to their new corresponding priority level.
        result[color] = _.get(_.find(highPriorities, { level: i }), 'level', _.last(highPriorities)!.level);
      },
      {} as Record<string, number>,
    )
    .value();

  // Needed because some old scorecards somehow have some of their colors as strings instead of hex codes
  const thresholds = _.map(metric.thresholds, (threshold: any) => {
    // yellow yields a different hex code
    const colorObj = threshold.color === 'yellow' ? tinycolor(METRIC_COLORS[1]) : tinycolor(threshold.color);
    const color = colorObj.isValid() ? colorObj.toHexString() : threshold.color;
    return { ...threshold, color };
  });

  const tuples = _.chain(thresholds)
    .transform(
      (result: Array<[number, number]>, threshold: any, i) => {
        // Last one will not have a threshold value
        if (i === thresholds.length - 1) {
          return;
        }

        const currentColorIndex = _.indexOf(lowercaseMetricColors, threshold.color);
        const nextColorIndex = _.indexOf(lowercaseMetricColors, thresholds[i + 1].color);
        // Can tell if the priorities are high or low since the old METRIC_COLORS went from high to low
        const isHigh = currentColorIndex <= nextColorIndex;
        const color = lowercaseMetricColors[isHigh ? currentColorIndex : nextColorIndex];
        // White thresholds that were not used as neutral will not be in the map
        if (_.has(priorityConversionMap, color)) {
          const level = priorityConversionMap[color] * (isHigh ? 1 : -1);
          result.push([level, threshold.threshold]);
        }
      },
      [] as Array<[number, number]>,
    )
    .uniqBy(_.head)
    .value() as Array<[number, number]>;

  return tuples.map(([level, threshold]) => `${level}=${threshold}`);
}

/**
 * Error handler for when fetching the table data fails. It checks to see if changing the homogenize units setting
 * would help. Otherwise, it tries to determine which items that were used in the table builder formula are
 * causing the problem by fetching each one individually. This is expensive, but there's no other good way to
 * figure it out.
 *
 * @param error - The error that arose from fetching the data
 * @param mode - The current table builder mode
 * @param items - The items that were used to generate the table builder formula
 */
export function catchTableBuilderDataFailure(error: any, mode: TableBuilderMode, items: any[]) {
  let apiMessage: string | undefined;
  let fetchFailedMessage: string | undefined = formatApiError(error).replace(/\((GET|POST) .*?\)/, '');
  const isHomogenizeError = HOMOGENIZE_UNIT_FORMULA_EXCEPTIONS.some((err) => fetchFailedMessage?.includes(err));
  const isAssetError = fetchFailedMessage?.includes('asset');
  const shouldFallbackToUnitless = !!sqTableBuilderStore.assetId && isHomogenizeError;

  if (shouldFallbackToUnitless) {
    warnToast({
      messageKey: 'TABLE_BUILDER.INCOMPATIBLE_UNITS_ACROSS_ASSETS',
      messageParams: { message: fetchFailedMessage },
    });
    flux.dispatch('TABLE_BUILDER_ADD_COLUMN', {
      column: COLUMNS_AND_STATS.valueUnitOfMeasure,
    });
    return exposedForTesting.setHomogenizeUnits(false, true);
  } else {
    let checkFailureForEachItem = false;
    if (isCanceled(error)) {
      fetchFailedMessage = undefined; // Request will be retried so don't flash an error message
    } else {
      if (_.isEmpty(fetchFailedMessage)) {
        fetchFailedMessage = i18next.t('LOGIN_PANEL.FRONTEND_ERROR');
      } else if (fetchFailedMessage.includes('must all be from a single asset')) {
        fetchFailedMessage += `\n\n${i18next.t('TABLE_BUILDER.REMOVE_ITEMS_SAME_ASSET')}`;
      } else if (error?.data?.errorCategory === 'ASSET') {
        fetchFailedMessage = i18next.t('TABLE_BUILDER.MISSING_ASSET_ASSOCIATION');
        checkFailureForEachItem = true;
      } else if (!isAssetError && !isHomogenizeError && error?.data?.errorCategory !== 'ASSET') {
        // Sometimes we hit errors due to unit mismatches in the table data, so we provide the API error message in
        // addition to our fetch error message to give more information to the user
        apiMessage = fetchFailedMessage;
        fetchFailedMessage = i18next.t('TABLE_BUILDER.FETCH_ERROR');
        checkFailureForEachItem = true;
      }
    }

    if (checkFailureForEachItem && !isPresentationWorkbookMode()) {
      _.forEach(items, (item) => {
        canFetchData(item, getDurationDisplayRange(), DATA_CANCELLATION_GROUP)
          .then(() => {
            setDataStatusPresentInTrendStores({ id: item.id });
          })
          .catch((e) => catchItemDataFailure(item.id, DATA_CANCELLATION_GROUP, e));
      });
    } else {
      _.forEach(items, (item) => catchItemDataFailure(item.id, DATA_CANCELLATION_GROUP, error));
    }

    flux.dispatch('TABLE_BUILDER_SET_FETCH_FAILED_MESSAGE', {
      fetchFailedMessage,
      mode,
      apiMessage,
    });
  }
}

/**
 * Remove items from the table without removing them from other stores
 * @param items - items to remove
 */
export function removeItemsFromTable(items: any[]) {
  flux.dispatch('TABLE_BUILDER_REMOVE_ITEMS', { items });
}

/**
 * Fetches and dispatches the simple table data that's backed by a FORMULA_ACROSS Materialized Table.
 * @param includeAncestors - When true, passes includeAncestors to the simple-table API so ancestor columns are included.
 * @param enableGrouping - When true with includeAncestors, enables row grouping on asset path columns added from the response.
 * @return A promise that resolves when the table has been fetched
 */
export async function fetchFormulaAcrossSimpleTableData(
  includeAncestors = false,
  enableGrouping = false,
): Promise<void> {
  const viewCapsule = getCapsuleFormula(getDurationDisplayRange());
  const formula = `group(${viewCapsule}).toTable('simple')`;

  const statistics = sqTableBuilderStore.columns.filter((column) => column.stat).map((column) => column.stat!);

  const itemPropertiesToInclude = sqTableBuilderStore.columns
    .filter((column) => column.backingTableColumn?.includes('item id.properties'))
    .map((column) => column.key);

  const assetPropertiesToInclude = sqTableBuilderStore.columns
    .filter((column) => column.backingTableColumn?.includes('Asset.properties'))
    .map((column) => {
      if (column.key === COLUMNS_AND_STATS.asset.key) {
        return NAME_ITEM_PROPERTY;
      } else {
        return column.key;
      }
    });

  const datasourcePropertiesToInclude = sqTableBuilderStore.columns
    .filter((column) => column.backingTableColumn?.includes('Datasource.properties'))
    .map((column) => {
      if (column.key === COLUMNS_AND_STATS.datasourceName.key) {
        return NAME_ITEM_PROPERTY;
      } else {
        return column.key;
      }
    });

  const propertiesToInclude: PropertiesForItemUUIDColumn[] = [];
  if (itemPropertiesToInclude.length > 0) {
    propertiesToInclude.push({
      uuidColumn: SeeqNames.MaterializedTables.ItemIdColumn,
      propertyNames: [...itemPropertiesToInclude, SeeqNames.Properties.AggregationFunction],
    });
  }
  if (assetPropertiesToInclude.length > 0) {
    propertiesToInclude.push({
      uuidColumn: SeeqNames.MaterializedTables.FormulaAcross.AssetColumn,
      propertyNames: assetPropertiesToInclude,
    });
  }

  if (datasourcePropertiesToInclude.length > 0) {
    propertiesToInclude.push({
      uuidColumn: SeeqNames.MaterializedTables.FormulaAcross.DatasourceColumn,
      propertyNames: datasourcePropertiesToInclude,
    });
  }

  const columnsToInclude: string[] = [
    SeeqNames.MaterializedTables.ItemIdColumn,
    SeeqNames.MaterializedTables.FormulaAcross.AssetColumn,
    SeeqNames.MaterializedTables.FormulaAcross.PathColumn,
    SeeqNames.MaterializedTables.FormulaAcross.ValueUomColumn,
    SeeqNames.MaterializedTables.FormulaAcross.DatasourceColumn,
    SeeqNames.MaterializedTables.FormulaAcross.SourceColumnIdColumn,
  ];

  const tableFormulas: TableFormula[] = [
    {
      formula,
      parameters: ['column_item=item id'],
      statistics: [...statistics, 'metricValue()'],
    },
  ];

  const items = sqTableBuilderStore.getTableItems();
  const itemIds = items.map((item) => item.id);
  itemIds.forEach((id) => {
    setDataStatusLoadingInTrendStores(id);
  });
  try {
    const { data: response } = await sqTablesApi.querySimpleTable(
      {
        searchInput: getTableSearchInput(),
        columnsToInclude,
        propertiesToInclude,
        tableFormulas,
        limit: MAX_TABLE_ROWS,
        includeAncestors,
      },
      { cancellationGroup: DATA_CANCELLATION_GROUP },
    );

    removeExistingAssetPathColumns();
    if (includeAncestors && response?.headers) {
      const searchAncestorColumnName = new RegExp(
        `^${SeeqNames.MaterializedTables.FormulaAcross.SearchAncestorPrefix}\\s\\d+$`,
      );
      const assetPathColumns = response.headers.filter(
        (header) => header.name != null && searchAncestorColumnName.test(header.name),
      );
      const ancestorCount = assetPathColumns.length;
      const newAssetPaths = createAssetPathConfigs(ancestorCount);
      setAssetPaths(newAssetPaths);
      const addedColumns = addAssetPathColumnsWithHeaders(newAssetPaths);
      if (enableGrouping) {
        enableAssetPathGrouping(addedColumns);
      }
    }

    flux.dispatch('TABLE_BUILDER_PUSH_FORMULA_ACROSS_SIMPLE_TABLE_DATA', {
      table: { data: response?.rows ?? [], headers: response?.headers ?? [] },
      trendItems: items,
    });
  } catch (e) {
    if (!isCanceled(e)) {
      errorToast({ httpResponseOrError: e });
    }
  } finally {
    itemIds.forEach((id) => setDataStatusPresentInTrendStores({ id }));
  }
}

export async function fetchFormulaAcrossConditionTableData(): Promise<void> {
  const start = getDurationDisplayRange().start.toISOString();
  const end = getDurationDisplayRange().end.toISOString();

  const statColumns = sqTableBuilderStore.columns.filter(
    (column) => column.stat && !column.signalId?.startsWith(DUMMY_ITEM_PREFIX),
  );

  // A stat's picked signal may itself be a cell of one of the scaling tables backing this condition table's rows --
  // resolve against every distinct table, since which table backs a given row varies per row (CRAB-59602).
  const [scalingItems] = getScalingAndRegularTableItems();
  const scalingTableIds = [...new Set(scalingItems.map((item) => item.tableId).filter((id): id is string => !!id))];
  const statSignalIds = statColumns.map((column) => column.signalId!);
  const resolvedByTable = await Promise.all(
    scalingTableIds.map((tableId) => resolveScalingColumnNamesForItems(tableId, statSignalIds)),
  );
  const resolvedColumnNames = new Map<string, string>();
  resolvedByTable.forEach((resolvedForTable) =>
    resolvedForTable.forEach((columnName, itemId) => resolvedColumnNames.set(itemId, columnName)),
  );

  // Keyed by label alone, not label+suffix: so a header matched back to its store column once its own suffix is also taken into account below.
  const statLabelToColumns = new Map<string, (typeof statColumns)[number][]>();
  const seenRequestStats = new Set<string>();
  const conditionStats: ConditionTableStat[] = [];
  statColumns.forEach((column) => {
    const resolvedColumnName = resolvedColumnNames.get(column.signalId!);
    const label = resolvedColumnName ?? column.signalId!;
    statLabelToColumns.set(label, [...(statLabelToColumns.get(label) ?? []), column]);

    // Two store columns can resolve to the same columnName + statistic (e.g. two sibling cells of one scaling
    // column both averaged) -- send that pair only once, or the server rejects the request as a duplicate column.
    const requestKey = `${column.stat}::${label}`;
    if (seenRequestStats.has(requestKey)) {
      return;
    }
    seenRequestStats.add(requestKey);
    conditionStats.push(
      resolvedColumnName
        ? { statistic: column.stat!, columnName: resolvedColumnName }
        : { statistic: column.stat!, itemId: column.signalId },
    );
  });

  const capsuleProperties = sqTableBuilderStore.columns
    .filter((column) => column.type === 'capsuleProperty')
    .map((column) => column.key);

  const conditionProperties = sqTableBuilderStore.columns
    .filter(
      (column) =>
        column.backingTableColumn?.includes('item id.properties') &&
        column.type !== 'capsuleProperty' &&
        column.key !== 'ID',
    )
    .map((column) => column.key);

  // For each metric we construct a fake metric in the store to allow us to skip fetching each returned metric
  // manually. This is the minimal list of properties to fake out that metric
  const necessaryMetricProperties = ['name'];
  necessaryMetricProperties.forEach((property) => {
    if (!conditionProperties.includes(property)) {
      conditionProperties.push(property);
    }
  });

  const assetPropertiesToInclude = sqTableBuilderStore.columns
    .filter((column) => column.backingTableColumn?.includes('Asset.properties'))
    .map((column) => {
      if (column.key === COLUMNS_AND_STATS.asset.key) {
        return NAME_ITEM_PROPERTY;
      } else {
        return column.key;
      }
    });

  const propertiesToInclude: PropertiesForItemUUIDColumn[] = [];
  if (assetPropertiesToInclude.length > 0) {
    propertiesToInclude.push({
      uuidColumn: SeeqNames.MaterializedTables.FormulaAcross.AssetColumn,
      propertyNames: assetPropertiesToInclude,
    });
  }

  const itemIds = sqTableBuilderStore.getTableItems().map((item) => item.id);
  try {
    itemIds.forEach((id) => {
      setDataStatusLoadingInTrendStores(id);
    });
    const { data: response } = await sqTablesApi.queryConditionTable(
      {
        conditionSource: {
          tableSearchSource: {
            searchInput: getTableSearchInput(),
            propertiesToInclude,
            conditionStats,
          },
        },
        start: { value: start },
        end: { value: end },
        conditionProperties,
        capsuleProperties,
        limit: MAX_CONDITION_TABLE_CAPSULES,
      },
      { cancellationGroup: DATA_CANCELLATION_GROUP },
    );

    // Identify UOM columns and create mapping before processing headers
    const uomColumnMap = new Map<string, number>();
    const uomColumnIndices = new Set<number>();
    const uomValues = new Map<string, string>(); // Map of column name to UOM value

    response!.headers!.forEach((header, index) => {
      if (header.name!.endsWith(' uom')) {
        const baseColumnName = header.name!.replace(' uom', '');
        uomColumnMap.set(baseColumnName, index);
        uomColumnIndices.add(index);

        // Extract UOM value from first non-null row
        const firstRowWithUom = response!.rows!.find((row) => row[index]);
        const uomValue = firstRowWithUom ? String(firstRowWithUom[index] || '') : '';
        uomValues.set(baseColumnName, uomValue);
      }
    });

    // Filter out UOM column values from rows
    response!.rows = response!.rows!.map((row) => row.filter((_, index) => !uomColumnIndices.has(index)));

    // Process headers: filter out UOM columns and apply transformations
    // Several store columns can share one response column (same columnName+statistic, deduped on request) --
    // track every such key against that column's index so they all read the one value back.
    const duplicateStatKeysByIndex = new Map<number, string[]>();
    response!.headers = response!
      .headers!.filter((header) => !header.name!.endsWith(' uom')) // Filter out UOM columns
      .map((header, index) => {
        const originalName = header.name!;
        let name = originalName.replace(/^item id\.properties\./, '');
        if (name === 'name') {
          name = 'nameExpression';
        } else if (name === 'Asset.properties.name') {
          name = 'asset';
        } else if (name === 'conditionTable_itemId') {
          // We always return the conditionTable_itemId column, but we'll change that to the ID so that if we request
          // the ID column it doesn't need to get fetched.
          name = 'ID';
        } else {
          // A stat column's header is "<label> <Suffix>". <label> is either a fixed signal id (never contains a
          // space) or a scaling table's columnName (may contain spaces), so split on the LAST space rather than
          // the first.
          const lastSpaceIndex = header.name!.lastIndexOf(' ');
          const label = lastSpaceIndex === -1 ? header.name! : header.name!.substring(0, lastSpaceIndex);
          const stat = lastSpaceIndex === -1 ? '' : header.name!.substring(lastSpaceIndex + 1);
          const candidates = statLabelToColumns.get(label);
          const matches = candidates?.filter((column) => column.columnSuffix === stat) ?? [];
          if (matches.length > 0) {
            name = matches[0].key;
            if (matches.length > 1) {
              duplicateStatKeysByIndex.set(
                index,
                matches.slice(1).map((column) => column.key),
              );
            }
          } else if (candidates) {
            name = `statistics.${stat.toLowerCase()}_${label}`;
          } else if (name.includes('_color')) {
            name = name.replace('_color', '_priorityColor');
          }
        }

        // Update uomValues map to use the transformed name as the key
        if (uomValues.has(originalName)) {
          const uomValue = uomValues.get(originalName)!;
          uomValues.delete(originalName);
          uomValues.set(name, uomValue);
        }

        return {
          ...header,
          name,
        };
      });

    const mapColumnKeysToColumnIndex: Record<string, number> = {};
    response!.headers!.forEach((header, index) => {
      mapColumnKeysToColumnIndex[header.name!] = index;
      duplicateStatKeysByIndex.get(index)?.forEach((key) => {
        mapColumnKeysToColumnIndex[key] = index;
      });
    });

    const structuredTable: CapsuleFormulaTableRow[] = response!.rows!.map((row) => {
      const indexOfCapsuleId = _.findIndex(response!.headers, {
        name: SeeqNames.CapsuleProperties.CapsuleId,
      });

      if (indexOfCapsuleId >= 0 && !row[indexOfCapsuleId]) {
        row[indexOfCapsuleId] = generateTemporaryId();
      }

      const newRow = _.transform(
        mapColumnKeysToColumnIndex,
        (rowObject, index: number, key) => {
          rowObject[key] = row[index];
        },
        {} as AnyProperty,
      );

      newRow.startTime = newRow.Start ? nanosToMillis(newRow.Start) : null;
      newRow.endTime = newRow.End ? nanosToMillis(newRow.End) : null;

      if (newRow.cursorKey === -1) {
        newRow.cursorKey = null;
      }

      return newRow as CapsuleFormulaTableRow;
    });

    const headers: { name: string; type: string; units: string }[] = response!.headers!.map((header) => {
      return {
        name: header.name!,
        type: header.type === TypeEnum.NUMERIC ? 'number' : 'string',
        units: uomValues.get(header.name!) || '',
      };
    });

    const presentMetricIds = new Set(
      headers.filter((header) => header.name.includes('_priorityName')).map((header) => header.name.split('_')[0]),
    );

    sqTableBuilderStore.columns
      .filter((column) => column.metricId)
      .forEach((column) => {
        if (!presentMetricIds.has(column.metricId!)) {
          exposedForTesting.removeColumn(column.key);
        }
      });

    presentMetricIds.forEach((metricId) => {
      const firstMetricRow: CapsuleFormulaTableRow | undefined = structuredTable.find(
        (row) => (row as any)['ID'] === metricId,
      );
      flux.dispatch('TABLE_BUILDER_ADD_METRIC_TO_FORMULA_ACROSS_CONDITION_TABLE', {
        metricId,
        item: {
          id: metricId,
          name: (firstMetricRow as any)['nameExpression'],
        },
      });
    });

    flux.dispatch('TABLE_BUILDER_PUSH_CONDITION_DATA', {
      headers,
      table: structuredTable,
      itemColumnsMap: tableBuilder.metricsToItemColumnsMap([...presentMetricIds].map((id) => ({ id }))),
      customPropertyName: '',
    });
  } catch (e) {
    if (!isCanceled(e)) {
      errorToast({ httpResponseOrError: e });
    }
  } finally {
    itemIds.forEach((id) => {
      setDataStatusPresentInTrendStores({ id });
    });
  }
}

export const getRowIds = (scalingItems: TableBuilderItem[]): string[] => {
  return [
    ...new Set(
      scalingItems.map((item) => {
        if (item.datumId !== undefined) {
          return `${item.tableId}|${item.datumId}`;
        } else {
          return item.rowId as string;
        }
      }),
    ),
  ];
};

export function getScalingAndRegularTableItems(): [TableBuilderItem[], TableBuilderItem[]] {
  return _.partition(sqTableBuilderStore.getTableItems(), (item) => isScalingTableItem(item));
}

function getTableSearchInput(): TableSearchInputV1 {
  const [scalingItems, fixedItems] = getScalingAndRegularTableItems();
  const fixedItemIds = fixedItems.map((item) => item.id);

  const itemsByTable = _.groupBy(scalingItems, (item) => item.tableId);
  const tableSources = Object.entries(itemsByTable).map(([tableId, items]) => {
    const columnIds = [...new Set(items.map((item) => item.columnId!))];
    const rowIds = sqTableBuilderStore.isFormulaAcrossTable ? [] : getRowIds(items);

    return {
      sourceTableId: tableId,
      columnIds,
      rowIds,
    };
  });

  return { tableSources, fixedItemIds };
}

/**
 * Refreshes the table formatting with updated trend items without fetching new data from the backend.
 * This is used when cosmetic properties (NumberFormat, StringFormat, DisplayUnit) change.
 */
export function refreshTableFormatting() {
  const mode = sqTableBuilderStore.mode;

  switch (mode) {
    case TableBuilderMode.Simple:
      if (sqTableBuilderStore.isFormulaAcrossTable) {
        flux.dispatch('TABLE_BUILDER_REFRESH_FORMULA_ACROSS_TABLE_FORMATTING');
      } else {
        flux.dispatch('TABLE_BUILDER_REFRESH_SIMPLE_TABLE_FORMATTING');
      }
      break;
    case TableBuilderMode.Condition:
      flux.dispatch('TABLE_BUILDER_REFRESH_CONDITION_TABLE_FORMATTING');
      break;
    default:
      break;
  }
}

export const exposedForTesting = {
  setShowChartView,
  fetchTable,
  setHomogenizeUnits,
  setAssetId,
  fetchOldMetrics,
  fetchConditionTableData,
  fetchConditionTableDataViaComputeCapsuleTable,
  fetchConditionTableDataViaConditionApi,
  normalizeConditionTableResponse,
  fetchSimpleTableData,
  catchTableBuilderDataFailure,
  fetchSimpleTableDistinctStringValues,
  setIsMigrating,
  setMode,
  computeColorForOldMetric,
  removeOldMetric,
  getThresholds,
  getCorrectWarningLogs,
  setHeadersType,
  getName,
  removeColumn,
  toggleColumn,
  addTextColumn,
  moveColumn,
  setColumnBackground,
  setColumnTextAlign,
  setColumnTextColor,
  setColumnTextStyle,
  setHeaderBackground,
  setHeaderTextAlign,
  setHeaderTextColor,
  setHeaderTextStyle,
  setStyleToAllColumns,
  setStyleToAllHeaders,
  setStyleToAllHeadersAndColumns,
  copyStyle,
  pasteStyleOnHeader,
  pasteStyleOnColumn,
  pasteStyleOnHeaderAndColumn,
  setCellText,
  setHeaderText,
  setHeadersFormat,
  setHeadersProperty,
  setIsTableStriped,
  setColumnFilter,
  fetchFormulaAcrossSimpleTableData,
  fetchFormulaAcrossConditionTableData,
};
